import { jsPDF } from 'jspdf'

interface CalendarEvent {
  id: string
  dealId: number
  dealName: string
  companyName: string
  type: 'go_live' | 'production_access' | 'custom_work' | 'deliverable' | 'task'
  title: string
  date: Date
  description?: string
  metadata?: {
    category?: string
    status?: string
    jiraTickets?: Array<{ ticket: string; status: string }>
    isHardDate?: boolean
  }
}

export function generateCalendarPDF(events: CalendarEvent[], month: Date): void {
  const doc = new jsPDF()
  const pageWidth = doc.internal.pageSize.getWidth()
  const pageHeight = doc.internal.pageSize.getHeight()
  const monthName = month.toLocaleString('default', { month: 'long', year: 'numeric' })
  const margin = 15

  // Header background
  doc.setFillColor(249, 250, 251) // gray-50
  doc.rect(0, 0, pageWidth, 45, 'F')

  // Title
  doc.setFontSize(24)
  doc.setFont('helvetica', 'bold')
  doc.setTextColor(17, 24, 39) // gray-900
  doc.text('Production Planning Calendar', pageWidth / 2, 18, { align: 'center' })

  doc.setFontSize(16)
  doc.setFont('helvetica', 'normal')
  doc.setTextColor(107, 114, 128) // gray-500
  doc.text(monthName, pageWidth / 2, 28, { align: 'center' })

  let yPos = 55

  // Summary boxes
  const goLives = events.filter((e) => e.type === 'go_live')
  const productionKeys = events.filter((e) => e.type === 'production_access')
  const customWorks = events.filter((e) => e.type === 'custom_work')
  const deliverables = events.filter((e) => e.type === 'deliverable')
  const tasks = events.filter((e) => e.type === 'task')

  const boxWidth = (pageWidth - margin * 2 - 12) / 5
  const boxHeight = 18
  const boxY = yPos

  // Helper to draw summary box
  const drawSummaryBox = (x: number, color: number[], label: string, count: number) => {
    // White background with colored border
    doc.setFillColor(255, 255, 255)
    doc.setDrawColor(color[0], color[1], color[2])
    doc.setLineWidth(1)
    doc.roundedRect(x, boxY, boxWidth, boxHeight, 2, 2, 'FD')

    // Count
    doc.setFontSize(18)
    doc.setFont('helvetica', 'bold')
    doc.setTextColor(color[0], color[1], color[2])
    doc.text(count.toString(), x + boxWidth / 2, boxY + 8, { align: 'center' })

    // Label
    doc.setFontSize(8)
    doc.setFont('helvetica', 'normal')
    doc.setTextColor(100, 100, 100) // gray text for label
    doc.text(label, x + boxWidth / 2, boxY + 14, { align: 'center' })
  }

  drawSummaryBox(margin, [34, 197, 94], 'Go Lives', goLives.length)
  drawSummaryBox(margin + boxWidth + 3, [59, 130, 246], 'Prod Keys', productionKeys.length)
  drawSummaryBox(margin + (boxWidth + 3) * 2, [168, 85, 247], 'Custom Work', customWorks.length)
  drawSummaryBox(margin + (boxWidth + 3) * 3, [249, 115, 22], 'Deliverables', deliverables.length)
  drawSummaryBox(margin + (boxWidth + 3) * 4, [225, 29, 72], 'Tasks', tasks.length)

  yPos = boxY + boxHeight + 18

  // Sort all events by date
  const sortedEvents = [...events].sort((a, b) => a.date.getTime() - b.date.getTime())

  // Helper to get event type label and color
  const getEventTypeInfo = (type: CalendarEvent['type']) => {
    switch (type) {
      case 'go_live':
        return { label: 'Go Live', color: [34, 197, 94], bgColor: [220, 252, 231] } // green
      case 'production_access':
        return { label: 'Prod Keys', color: [59, 130, 246], bgColor: [219, 234, 254] } // blue
      case 'custom_work':
        return { label: 'Custom Work', color: [168, 85, 247], bgColor: [243, 232, 255] } // purple
      case 'deliverable':
        return { label: 'Deliverable', color: [249, 115, 22], bgColor: [255, 237, 213] } // orange
      case 'task':
        return { label: 'Task', color: [225, 29, 72], bgColor: [255, 228, 230] } // rose
    }
  }

  // Section divider line
  doc.setDrawColor(229, 231, 235) // gray-200
  doc.setLineWidth(0.5)
  doc.line(margin, yPos, pageWidth - margin, yPos)
  yPos += 10

  doc.setFont('helvetica', 'normal')
  doc.setFontSize(9)
  doc.setTextColor(17, 24, 39)

  // Add each event
  sortedEvents.forEach((event) => {
    const eventHeight = 24 // Estimated height per event

    // Check if we need a new page
    if (yPos + eventHeight > pageHeight - 25) {
      doc.addPage()
      yPos = 20
    }

    const dateStr = event.date.toLocaleDateString('default', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })

    const typeInfo = getEventTypeInfo(event.type)
    const eventBoxWidth = pageWidth - margin * 2
    const eventBoxHeight = 20

    // Event background box
    doc.setFillColor(typeInfo.bgColor[0], typeInfo.bgColor[1], typeInfo.bgColor[2])
    doc.setDrawColor(typeInfo.color[0], typeInfo.color[1], typeInfo.color[2])
    doc.setLineWidth(0.5)
    doc.roundedRect(margin, yPos, eventBoxWidth, eventBoxHeight, 1.5, 1.5, 'FD')

    // Colored accent bar on left
    doc.setFillColor(typeInfo.color[0], typeInfo.color[1], typeInfo.color[2])
    doc.roundedRect(margin, yPos, 3, eventBoxHeight, 1.5, 1.5, 'F')

    let contentX = margin + 8
    let contentY = yPos + 6

    // Date
    doc.setFontSize(9)
    doc.setFont('helvetica', 'bold')
    doc.setTextColor(55, 65, 81) // gray-700
    doc.text(dateStr, contentX, contentY)

    // Event type badge with white background and colored border
    const badgeX = contentX + 48
    const badgeWidth = 24
    doc.setFillColor(255, 255, 255)
    doc.setDrawColor(typeInfo.color[0], typeInfo.color[1], typeInfo.color[2])
    doc.setLineWidth(0.5)
    doc.roundedRect(badgeX, contentY - 3.5, badgeWidth, 5, 1, 1, 'FD')

    doc.setFontSize(7)
    doc.setFont('helvetica', 'bold')
    doc.setTextColor(typeInfo.color[0], typeInfo.color[1], typeInfo.color[2])
    doc.text(typeInfo.label.toUpperCase(), badgeX + badgeWidth / 2, contentY, { align: 'center' })

    // Hard date badge (red)
    if (event.metadata?.isHardDate) {
      const hardBadgeX = badgeX + badgeWidth + 3
      const hardBadgeWidth = 22
      doc.setFillColor(254, 226, 226) // red-100
      doc.setDrawColor(220, 38, 38) // red-600
      doc.setLineWidth(0.5)
      doc.roundedRect(hardBadgeX, contentY - 3.5, hardBadgeWidth, 5, 1, 1, 'FD')
      doc.setFontSize(6)
      doc.setFont('helvetica', 'bold')
      doc.setTextColor(220, 38, 38)
      doc.text('HARD DATE', hardBadgeX + hardBadgeWidth / 2, contentY, { align: 'center' })
    }

    contentY += 5.5

    // Company name
    doc.setFontSize(10)
    doc.setFont('helvetica', 'bold')
    doc.setTextColor(17, 24, 39) // gray-900
    const truncatedCompany = event.companyName.length > 45
      ? event.companyName.substring(0, 45) + '...'
      : event.companyName
    doc.text(truncatedCompany, contentX, contentY)

    // Event title (if different from type)
    if (event.title && event.title !== 'Go Live' && event.title !== 'Production Keys') {
      contentY += 5
      doc.setFontSize(8)
      doc.setFont('helvetica', 'italic')
      doc.setTextColor(107, 114, 128) // gray-500
      const truncatedTitle = event.title.length > 60
        ? event.title.substring(0, 60) + '...'
        : event.title
      doc.text(truncatedTitle, contentX, contentY)
    }

    yPos += eventBoxHeight + 3 // Space between events
  })

  // Footer on all pages
  const pageCount = doc.getNumberOfPages()
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i)

    // Footer line
    const footerY = pageHeight - 18
    doc.setDrawColor(229, 231, 235)
    doc.setLineWidth(0.3)
    doc.line(margin, footerY, pageWidth - margin, footerY)

    // Footer text
    doc.setFontSize(7)
    doc.setFont('helvetica', 'normal')
    doc.setTextColor(156, 163, 175) // gray-400
    doc.text(
      `Generated ${new Date().toLocaleDateString('default', { month: 'short', day: 'numeric', year: 'numeric' })}`,
      margin,
      footerY + 5
    )
    doc.text(
      `Page ${i} of ${pageCount}`,
      pageWidth - margin,
      footerY + 5,
      { align: 'right' }
    )
  }

  // Save
  doc.save(`production-calendar-${month.getFullYear()}-${month.getMonth() + 1}.pdf`)
}

export function generateSlackMessage(events: CalendarEvent[], month: Date): string {
  const now = new Date()
  const monthName = month.toLocaleString('default', { month: 'long', year: 'numeric' })
  const nextMonthName = new Date(month.getFullYear(), month.getMonth() + 1, 1).toLocaleString('default', { month: 'long', year: 'numeric' })
  const baseUrl = 'https://dealhub.pmgoncalves0.workers.dev'

  // Group events by type
  const goLives = events.filter((e) => e.type === 'go_live')
  const productionKeys = events.filter((e) => e.type === 'production_access')
  const customWorks = events.filter((e) => e.type === 'custom_work')
  const deliverables = events.filter((e) => e.type === 'deliverable')

  // Sort events by date
  const sortByDate = (a: CalendarEvent, b: CalendarEvent) => a.date.getTime() - b.date.getTime()
  goLives.sort(sortByDate)
  productionKeys.sort(sortByDate)
  customWorks.sort(sortByDate)
  deliverables.sort(sortByDate)

  let message = `*📅 Production Planning Calendar*\n`
  message += `*${monthName} & ${nextMonthName}*\n\n`
  message += `_✨ ${events.length} events scheduled in the next 2 months ✨_\n\n`

  // Go Lives
  if (goLives.length > 0) {
    message += `\n━━━━━━━━━━━━━━━━━━━━━━\n`
    message += `*🚀 Go Live Dates (${goLives.length})*\n`
    goLives.forEach((event) => {
      const dateStr = event.date.toLocaleDateString('default', { month: 'short', day: 'numeric' })
      const dealUrl = `${baseUrl}/scope?session=${event.dealId}`
      const hardBadge = event.metadata?.isHardDate ? ' ⚠️ HARD DATE' : ''
      const icon = event.metadata?.isHardDate ? '🚨' : '🎯'
      message += `${icon} *${dateStr}* - <${dealUrl}|${event.companyName}>${hardBadge}\n`
    })
  }

  // Production Keys
  if (productionKeys.length > 0) {
    message += `\n━━━━━━━━━━━━━━━━━━━━━━\n`
    message += `*🔑 Production Keys Delivery (${productionKeys.length})*\n`
    productionKeys.forEach((event) => {
      const dateStr = event.date.toLocaleDateString('default', { month: 'short', day: 'numeric' })
      const dealUrl = `${baseUrl}/scope?session=${event.dealId}`
      message += `🔐 *${dateStr}* - <${dealUrl}|${event.companyName}>\n`
    })
  }

  // Custom Work Items
  if (customWorks.length > 0) {
    message += `\n━━━━━━━━━━━━━━━━━━━━━━\n`
    message += `*🔧 Custom Work Items (${customWorks.length})*\n`
    customWorks.forEach((event) => {
      const dateStr = event.date.toLocaleDateString('default', { month: 'short', day: 'numeric' })
      const cwIcon = event.metadata?.isHardDate ? '🚨' : '⚙️ '
      const cwHardBadge = event.metadata?.isHardDate ? ' ⚠️ HARD DATE' : ''
      message += `${cwIcon} *${dateStr}* - ${event.title} (${event.companyName})${cwHardBadge}`
      if (event.metadata?.jiraTickets && event.metadata.jiraTickets.length > 0) {
        const ticketLinks = event.metadata.jiraTickets
          .map((t) => {
            const key = t.ticket.replace(/^https?:\/\/.*\/browse\//, '')
            return `<https://frankieone.atlassian.net/browse/${key}|📋 ${key}>`
          })
          .join(', ')
        message += ` ${ticketLinks}`
      }
      message += `\n`
    })
  }

  // Deliverables
  if (deliverables.length > 0) {
    message += `\n━━━━━━━━━━━━━━━━━━━━━━\n`
    message += `*📦 Deliverables (${deliverables.length})*\n`
    deliverables.forEach((event) => {
      const dateStr = event.date.toLocaleDateString('default', { month: 'short', day: 'numeric' })
      message += `• *${dateStr}* - ${event.title}`
      if (event.metadata?.status) {
        message += ` [${event.metadata.status}]`
      }
      message += `\n`
    })
  }

  if (events.length === 0) {
    message += `\n🌴 _No events scheduled in the next 2 months - smooth sailing ahead!_ 🌴\n`
  }

  message += `\n━━━━━━━━━━━━━━━━━━━━━━\n`
  message += `_🤖 Posted on ${now.toLocaleDateString()} by DealHub Production Calendar_`

  return message
}
